import { Type } from '@sinclair/typebox';
import { getChannel } from '@ayocore/platform/messaging/client';
import { enqueueTask } from './rpc';
import {
  BackgroundTaskEventSchema,
  type BackgroundTaskEvent,
} from '../common/types/background-task-event';

/**
 * Канал состояний фоновых задач. Только приём — клиент ничего не шлёт.
 * Сервер доставляет лишь задачи текущего пользователя (sendMessage по created_by).
 */
const channel = getChannel('background-tasks').withSchema(
  Type.Unknown(),
  BackgroundTaskEventSchema
);

/** Прогресс задачи. */
export interface TaskProgress {
  percent: number;
  message: string;
}

/** Хэндл запущенной фоновой задачи: подписка на её состояние. */
export interface BackgroundTaskHandle {
  /** Любой переход публичного статуса, включая initial snapshot queued/running. */
  onStatus(cb: (status: BackgroundTaskEvent['status']) => void): this;
  /** Прогресс (percent + текущий message). */
  onProgress(cb: (progress: TaskProgress) => void): this;
  /** Новое текстовое сообщение о ходе (ctx.send). */
  onMessage(cb: (message: string) => void): this;
  /** Успешное завершение: результат задачи. */
  onDone(cb: (result: unknown) => void): this;
  /** Ошибка: текст. */
  onError(cb: (error: string) => void): this;
  /** Промис: resolve на done(result), reject на failed(error). */
  readonly done: Promise<unknown>;
  /** Отписаться от событий (саму задачу не отменяет — отмена пока не поддержана). */
  stop(): void;
}

/**
 * Запустить фоновую задачу и получить хэндл для отслеживания её состояния.
 *
 * Возвращается синхронно; enqueue летит внутри. Подписка на канал ставится
 * сразу, события до получения uuid буферизуются — гонки нет.
 *
 * @example
 * const task = executeBackgroundTask('mws::recalcHull', { projectId });
 * task.onProgress(({ percent, message }) => setBar(percent, message));
 * task.onMessage(text => appendLog(text));
 * task.onDone(result => console.log('готово', result));
 * task.onError(err => toast.error(err));
 * // или: const result = await task.done;
 */
export function executeBackgroundTask(type: string, payload?: unknown): BackgroundTaskHandle;
/**
 * Серверная постановка через терминальный метод репозитория.
 * Callback вызывается ПОСЛЕ подписки; не передавайте уже запущенный Promise.
 */
export function executeBackgroundTask(
  enqueue: () => Promise<{ uuid: string; task?: BackgroundTaskEvent }>
): BackgroundTaskHandle;
export function executeBackgroundTask(
  typeOrEnqueue: string | (() => Promise<{ uuid: string; task?: BackgroundTaskEvent }>),
  payload?: unknown
): BackgroundTaskHandle {
  const statusCbs: Array<(status: BackgroundTaskEvent['status']) => void> = [];
  const progressCbs: Array<(p: TaskProgress) => void> = [];
  const messageCbs: Array<(m: string) => void> = [];
  const doneCbs: Array<(r: unknown) => void> = [];
  const errorCbs: Array<(e: string) => void> = [];

  let uuid: string | null = null;
  const buffer: BackgroundTaskEvent[] = [];
  let lastMessage: string | undefined;
  let settled = false;

  let resolveDone: (result: unknown) => void = () => {};
  let rejectDone: (error: unknown) => void = () => {};
  const done = new Promise<unknown>((resolve, reject) => {
    resolveDone = resolve;
    rejectDone = reject;
  });
  // Не плодим unhandledrejection, если потребитель работает через onError, а не done.
  done.catch(() => {});

  const finish = (): void => {
    settled = true;
    unsubscribe();
  };

  const dispatch = (ev: BackgroundTaskEvent): void => {
    if (settled) return;
    statusCbs.forEach(cb => cb(ev.status));
    if (ev.percent !== undefined || ev.message !== undefined) {
      const progress: TaskProgress = { percent: ev.percent ?? 0, message: ev.message ?? '' };
      progressCbs.forEach(cb => cb(progress));
    }
    if (ev.message !== undefined && ev.message !== lastMessage) {
      lastMessage = ev.message;
      messageCbs.forEach(cb => cb(ev.message ?? ''));
    }
    if (settled) return;
    if (ev.status === 'done') {
      doneCbs.forEach(cb => cb(ev.result));
      resolveDone(ev.result);
      finish();
    } else if (ev.status === 'failed') {
      const error = ev.error ?? 'Задача завершилась с ошибкой';
      errorCbs.forEach(cb => cb(error));
      rejectDone(new Error(error));
      finish();
    }
  };

  const unsubscribe = channel.onMessage(ev => {
    if (uuid === null) {
      buffer.push(ev); // uuid ещё не известен — копим, проиграем после enqueue
      return;
    }
    if (ev.uuid === uuid) dispatch(ev);
  });

  // Микрозадача также превращает синхронный throw callback в отказ task.done.
  Promise.resolve()
    .then(() =>
      typeof typeOrEnqueue === 'function' ? typeOrEnqueue() : enqueueTask(typeOrEnqueue, payload)
    )
    .then(({ uuid: id, task: initial }) => {
      uuid = id;
      // Для coalesced-задачи нового WS create-event не будет. Snapshot из RPC
      // сразу восстанавливает queued/running/progress после повторного клика.
      // Он идёт ДО накопленного буфера: более новые WS-события не регрессируют.
      if (initial?.uuid === uuid) dispatch(initial);
      for (const ev of buffer) if (ev.uuid === uuid) dispatch(ev);
      buffer.length = 0;
    })
    .catch((err: unknown) => {
      if (settled) return;
      const message = err instanceof Error ? err.message : String(err);
      errorCbs.forEach(cb => cb(message));
      rejectDone(err);
      finish();
    });

  const handle: BackgroundTaskHandle = {
    onStatus(cb) {
      statusCbs.push(cb);
      return handle;
    },
    onProgress(cb) {
      progressCbs.push(cb);
      return handle;
    },
    onMessage(cb) {
      messageCbs.push(cb);
      return handle;
    },
    onDone(cb) {
      doneCbs.push(cb);
      return handle;
    },
    onError(cb) {
      errorCbs.push(cb);
      return handle;
    },
    done,
    stop() {
      unsubscribe();
    },
  };
  return handle;
}
